import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BrandLockup } from "../components/Brand";
import { ArrowLeft, Search, Music2 } from "lucide-react";
import { api } from "../lib/api";

const PACK_TITLES = {
  tarkan: "Tarkan Özel",
  baris_manco: "Barış Manço Özel",
  karisik: "Karışık Şarkılar",
};
const PACK_ACCENTS = {
  tarkan: "from-fuchsia-500/15 to-pink-500/5 border-fuchsia-400/25 text-fuchsia-200",
  baris_manco: "from-amber-400/15 to-yellow-500/5 border-amber-400/25 text-amber-200",
  karisik: "from-cyan-500/15 to-violet-500/5 border-cyan-400/25 text-cyan-200",
};

export default function IdiomShowcase() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [activePack, setActivePack] = useState("all");

  useEffect(() => {
    api.get("/idioms/public").then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLocaleLowerCase("tr-TR");
    return items.filter((it) => {
      if (activePack !== "all" && it.pack_key !== activePack) return false;
      if (!needle) return true;
      const hay = `${it.idiom} ${it.meaning} ${it.artist} ${it.song}`.toLocaleLowerCase("tr-TR");
      return hay.includes(needle);
    });
  }, [items, q, activePack]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const it of filtered) {
      const key = `${it.artist}||${it.song}`;
      if (!map.has(key)) map.set(key, { artist: it.artist, song: it.song, pack: it.pack_key, items: [] });
      map.get(key).items.push(it);
    }
    return Array.from(map.values());
  }, [filtered]);

  return (
    <div className="min-h-screen text-white relative"
         style={{ background: "linear-gradient(180deg, #070B14 0%, #0B1020 100%)" }}>
      <div className="hero-spectrum absolute inset-0" aria-hidden />
      <header className="relative z-10 px-6 sm:px-10 py-6 flex items-center justify-between">
        <BrandLockup />
        <Link to="/" data-testid="showcase-back-home"
              className="text-sm text-zinc-400 hover:text-cyan-300 inline-flex items-center gap-1">
          <ArrowLeft className="h-4 w-4"/> Ana Sayfa
        </Link>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-6 sm:px-10 pb-24" data-testid="idiom-showcase">
        <div className="text-cyan-300 font-heading tracking-widest text-xs">DEYİM VİTRİNİ</div>
        <h1 className="mt-2 font-heading text-5xl font-bold">
          30 deyim, <span className="font-literary italic text-cyan-300">15 şarkı</span>
        </h1>
        <p className="mt-3 text-zinc-400 font-literary italic text-lg max-w-2xl">
          Uygulamada yer alan tüm deyimlerin kısa önizlemesi. Sorular ve doğru cevaplar bu
          sayfada gösterilmez — yalnızca öğretmen hazırlığı içindir.
        </p>

        {/* Search + pack filter */}
        <div className="mt-8 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <label className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500"/>
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Deyim, anlam, şarkı veya sanatçı ara…"
              data-testid="showcase-search"
              className="w-full rounded-full bg-black/60 border border-white/10 focus:border-cyan-400 focus:outline-none pl-9 pr-4 py-3 text-sm"/>
          </label>
          <div className="flex flex-wrap gap-2">
            {["all", "tarkan", "baris_manco", "karisik"].map((k) => (
              <button key={k} onClick={() => setActivePack(k)} data-testid={`showcase-pack-${k}`}
                      className={`rounded-full px-4 py-2 text-xs font-heading tracking-widest border transition ${
                        activePack === k
                          ? "border-cyan-400 text-cyan-200 bg-cyan-500/10"
                          : "border-white/10 text-zinc-400 hover:border-white/30"
                      }`}>
                {k === "all" ? "TÜMÜ" : PACK_TITLES[k]?.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4 text-xs text-zinc-500">
          <span data-testid="showcase-count">{filtered.length}</span> / {items.length} deyim
          gösteriliyor
        </div>

        {/* Grouped grid */}
        <div className="mt-6 space-y-8">
          {grouped.map((g) => (
            <section key={`${g.artist}||${g.song}`} className={`rounded-3xl border bg-gradient-to-br ${PACK_ACCENTS[g.pack] || ""} p-6`}>
              <div className="flex items-center gap-3">
                <Music2 className="h-4 w-4"/>
                <div>
                  <div className="text-[10px] font-heading tracking-widest opacity-80">{g.artist.toUpperCase()}</div>
                  <div className="font-literary italic text-2xl text-white">{g.song}</div>
                </div>
                <div className="ml-auto text-xs opacity-70">{g.items.length} deyim</div>
              </div>
              <div className="mt-5 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {g.items.map((it) => (
                  <div key={it.id} className="rounded-2xl border border-white/10 bg-black/30 p-4"
                       data-testid={`idiom-card-${it.id}`}>
                    <div className="font-literary italic text-lg text-cyan-100">"{it.idiom}"</div>
                    <div className="mt-2 text-zinc-300 text-sm leading-snug">{it.meaning}</div>
                  </div>
                ))}
              </div>
            </section>
          ))}
          {grouped.length === 0 && (
            <div className="text-center text-zinc-500 py-16">Bu filtreye uyan deyim yok.</div>
          )}
        </div>
      </main>
    </div>
  );
}
